import { XMarkIcon, ChevronDownIcon, ChevronUpIcon, CheckCircleIcon, XCircleIcon } from "@heroicons/react/24/outline";
import { useContext, useEffect, useMemo, useState } from "react";
import { collectionsListName } from "src/api/config";
import { testRegistry, answersRegistry, questionsRegistry } from "src/api/config/notifications.config";
import { DrawerComponent } from "src/components/DialogComponent";
import { SearchUI } from "src/components/ui/Search";
import TGButton from "src/components/ui/TGButton";
import ListIcon from "src/components/ui-icons/reports/ListIcon";
import { useAppDispatch, useAppSelector } from "src/hooks/redux";
import { getItems } from "src/modules/spItems/actions";
import { getItemsRequest } from "src/modules/spItems/api";
import { selectItems } from "src/modules/spItems/selectors";
import { FlexCol } from "src/utils/flexLayouts";
import { twMerge } from "tailwind-merge";
import { TestContext } from "../components/Tests/store/testContext";
import { addQuestion, changeMinCorrectAnswersCount } from "../components/Tests/store/testActions";
import { IQuestion, ITesting } from "../components/Tests/types";

function parseStoredQuestions() {
  try {
    const value = localStorage.getItem("questions");
    return value ? JSON.parse(value) : [];
  } catch {
    return [];
  }
}

export default function NotificationTestsMobile({
  zIndex,
  closeTests,
  onResult,
}: {
  zIndex: number;
  closeTests: () => void;
  onResult: (data: ITesting) => void;
}) {
  const { state, dispatch } = useContext(TestContext);
  const [openRegistryDrawer, setOpenRegistryDrawer] = useState(false);

  useEffect(() => {
    localStorage.setItem("questions", JSON.stringify(state.Questions));
    localStorage.setItem(
      "minCorrectAnswersCount",
      JSON.stringify(state.MinCorrectAnswersCount),
    );
    onResult(state);
  }, [onResult, state]);

  useEffect(() => {
    if (!state.Questions.length) {
      return;
    }

    if (state.MinCorrectAnswersCount > state.Questions.length) {
      dispatch(changeMinCorrectAnswersCount(state.Questions.length));
      return;
    }

    if (state.MinCorrectAnswersCount < 1) {
      dispatch(changeMinCorrectAnswersCount(1));
    }
  }, [dispatch, state.MinCorrectAnswersCount, state.Questions.length]);

  return (
    <>
      <FlexCol className="min-h-full grow">
        <header className="flex items-start justify-between border-b px-5 pb-4 pt-5">
          <FlexCol className="min-w-0">
            <h1 className="text-[18px] font-medium leading-[28px] text-[#111827]">
              Тестирование
            </h1>
            <div className="mt-1 flex items-center gap-2 text-[14px] leading-[20px] text-[#637381]">
              <span className="h-2.5 w-2.5 rounded-full bg-[#637381]" />
              <span>
                Вопросов: {state.Questions.length}
                {!state.Questions.length ? ", не требуется" : ""}
              </span>
            </div>
          </FlexCol>
          <button type="button" onClick={closeTests} className="shrink-0 text-[#111827]">
            <XMarkIcon className="h-8 w-8" />
          </button>
        </header>

        <div className="grow px-5 py-5">
          {state.Questions.length > 0 && (
            <>
              <p className="mb-4 text-[16px] font-medium leading-[24px] text-[#637381]">
                Вопросы
              </p>
              <div className="mb-4 space-y-3">
                {state.Questions.map((question, index) => (
                  <QuestionCardMobile key={`${question.ID}-${index}`} question={question} />
                ))}
              </div>
            </>
          )}

          <TGButton
            className="mb-5 w-full justify-center border-[#E5E7EB] px-4 py-4 text-[#637381]"
            icon={<ListIcon />}
            onClick={() => setOpenRegistryDrawer(true)}
          >
            Добавить тест из реестра тестов
          </TGButton>

          <MinCorrectAnswersMobile />
        </div>

        <footer className="flex flex-col gap-3 border-t p-5">
          <TGButton
            color="bluedark"
            className="h-[44px] w-full justify-center text-[14px]"
            onClick={closeTests}
            disabled={!state.Questions.length}
          >
            Продолжить
          </TGButton>
          <TGButton
            className="h-[44px] w-full justify-center text-[14px]"
            onClick={closeTests}
          >
            Отмена
          </TGButton>
        </footer>
      </FlexCol>

      <TestRegistryDrawerMobile
        zIndex={zIndex + 10}
        open={openRegistryDrawer}
        closeDrawer={() => setOpenRegistryDrawer(false)}
      />
    </>
  );
}

function MinCorrectAnswersMobile() {
  const {
    dispatch,
    state: { Questions, MinCorrectAnswersCount },
  } = useContext(TestContext);

  if (!Questions.length) {
    return null;
  }

  return (
    <div>
      <p className="mb-3 text-[16px] font-normal leading-[24px] text-black">
        Необходимое количество правильных ответов для успешного прохождения
        теста
      </p>
      <select
        value={Math.min(Math.max(MinCorrectAnswersCount || 1, 1), Questions.length)}
        onChange={(e) => dispatch(changeMinCorrectAnswersCount(+e.target.value))}
        className="w-full rounded-md border border-[#DFE4EA] px-4 py-3 text-[16px] leading-[24px] text-black shadow-custom focus:border-[#DFE4EA] focus:outline-none"
      >
        {Questions.map((_, index) => (
          <option key={index + 1} value={index + 1}>
            {index + 1}
          </option>
        ))}
      </select>
    </div>
  );
}

function QuestionCardMobile({ question }: { question: IQuestion }) {
  return (
    <div className="rounded-lg bg-[#F3F4F6] p-4">
      <div className="mb-4 flex items-start gap-3">
        <span className="mt-1 shrink-0 text-[#637381]">
          <ListIcon />
        </span>
        <p className="text-[16px] font-medium leading-[24px] text-[#111827]">
          {question.Description}
        </p>
      </div>

      <div className="space-y-3">
        {question.Answers.map((answer) => (
          <div key={answer.ID} className="flex items-start gap-3 text-[14px] leading-[20px] text-[#637381]">
            <span className="mt-0.5 shrink-0">
              {answer.IsCorrect ? (
                <CheckCircleIcon className="h-5 w-5 text-[#9CA3AF]" />
              ) : (
                <XCircleIcon className="h-5 w-5 text-[#9CA3AF]" />
              )}
            </span>
            <span className="break-words">{answer.Description}</span>
          </div>
        ))}
      </div>
    </div>
  );
}

function TestRegistryDrawerMobile({
  zIndex,
  open,
  closeDrawer,
}: {
  zIndex: number;
  open: boolean;
  closeDrawer: () => void;
}) {
  const dispatch = useAppDispatch();
  const testsRegistryList = useAppSelector((state) => selectItems(state, testRegistry));
  const testContext = useContext(TestContext);
  const [collections, setCollections] = useState<any[]>([]);
  const [selectedCollection, setSelectedCollection] = useState("");
  const [testName, setTestName] = useState("");
  const [selectedTestId, setSelectedTestId] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);

  useEffect(() => {
    dispatch(
      getItems({
        listName: testRegistry,
      }),
    );
    getItemsRequest({ listName: collectionsListName }).then((results) =>
      setCollections(results?.items ?? []),
    );
  }, [dispatch]);

  useEffect(() => {
    if (!open) {
      setSelectedTestId("");
      setSelectedCollection("");
      setTestName("");
    }
  }, [open]);

  const filteredListTests = useMemo(() => {
    const cleanedTestName = testName.replace(/\s+/g, "").toLowerCase();

    return testsRegistryList.filter((test: any) => {
      const matchesName =
        cleanedTestName.length === 0 ||
        test.Title.replace(/\s+/g, "").toLowerCase().includes(cleanedTestName);
      const matchesCollection =
        !selectedCollection || test.Collections?.Title === selectedCollection;

      return matchesName && matchesCollection;
    });
  }, [selectedCollection, testName, testsRegistryList]);

  async function getAnswers(answers: any[]) {
    const answersList = await getItemsRequest({
      listName: answersRegistry,
      filters: { ID: answers },
    });

    return answersList.items.map((answer: any) => ({
      ID: String(answer.ID),
      Description: answer.Description,
      IsCorrect: answer.IsCorrect,
    }));
  }

  async function handleSelectTest() {
    if (!selectedTestId) {
      return;
    }

    const questionsTest = testsRegistryList.find(
      (test: any) => String(test.ID) === selectedTestId,
    );

    if (!questionsTest?.QuestionsID?.length) {
      closeDrawer();
      return;
    }

    setIsSubmitting(true);

    try {
      const questions = await getItemsRequest({
        listName: questionsRegistry,
        filters: {
          ID: questionsTest.QuestionsID.map((question: any) => question.ID),
        },
      });

      const testQuestions = await Promise.all(
        questions.items.map(async (question: any) => {
          const answers = await getAnswers(
            question.Answers.map((answer: any) => answer.get_lookupId()),
          );

          return {
            ID: String(question.ID),
            Description: question.Description,
            Type: question.TypeQuestion,
            Answers: answers,
          };
        }),
      );

      testQuestions.forEach((question) => testContext.dispatch(addQuestion(question)));
      closeDrawer();
    } finally {
      setIsSubmitting(false);
    }
  }

  return (
    <DrawerComponent
      percentage="87"
      zIndex={zIndex}
      open={open}
      toggleDrawer={(state) => {
        if (!state) {
          closeDrawer();
        }
      }}
    >
      <FlexCol className="min-h-full grow">
        <header className="flex items-start justify-between border-b px-5 pb-4 pt-5">
          <FlexCol className="min-w-0">
            <h1 className="text-[18px] font-medium leading-[28px] text-[#111827]">
              Тестирование
            </h1>
            <div className="mt-1 flex items-center gap-2 text-[14px] leading-[20px] text-[#637381]">
              <span className="h-2.5 w-2.5 rounded-full bg-[#637381]" />
              <span>Вопросов: {testContext.state.Questions.length}</span>
            </div>
          </FlexCol>
          <button type="button" onClick={closeDrawer} className="shrink-0 text-[#111827]">
            <XMarkIcon className="h-8 w-8" />
          </button>
        </header>

        <div className="grow px-5 py-5">
          <div className="mb-4">
            <label className="mb-2 block text-[14px] font-medium leading-[20px] text-black">
              Коллекция
            </label>
            <div className="relative">
              <select
                value={selectedCollection}
                onChange={(e) => setSelectedCollection(e.target.value)}
                className="w-full appearance-none rounded-md border border-[#DFE4EA] bg-white px-4 py-3 pr-10 text-[16px] leading-[24px] text-black shadow-custom outline-none"
              >
                <option value="">Выберите коллекцию</option>
                {collections.map((collection: any) => (
                  <option key={collection.ID} value={collection.Title}>
                    {collection.Title}
                  </option>
                ))}
              </select>
              <ChevronDownIcon className="pointer-events-none absolute right-3 top-1/2 h-5 w-5 -translate-y-1/2 text-[#637381]" />
            </div>
          </div>

          <div className="mb-4">
            <SearchUI
              label="Название теста"
              placeholder="Введите название теста"
              defaultValue={testName}
              onClick={setTestName}
              inputClass="pr-12"
            />
          </div>

          <div className="space-y-3">
            {filteredListTests.map((test: any) => (
              <RegistryTestItemMobile
                key={test.ID}
                test={test}
                checked={selectedTestId === String(test.ID)}
                onSelect={() => setSelectedTestId(String(test.ID))}
              />
            ))}

            {!filteredListTests.length && (
              <div className="py-6 text-center text-[14px] leading-[20px] text-[#637381]">
                Тесты не найдены
              </div>
            )}
          </div>
        </div>

        <footer className="flex flex-col gap-3 border-t p-5">
          <TGButton
            color="bluedark"
            className="h-[44px] w-full justify-center text-[14px]"
            onClick={handleSelectTest}
            disabled={!selectedTestId || isSubmitting}
            loading={isSubmitting}
          >
            Выбрать
          </TGButton>
          <TGButton
            className="h-[44px] w-full justify-center text-[14px]"
            onClick={closeDrawer}
          >
            Отмена
          </TGButton>
        </footer>
      </FlexCol>
    </DrawerComponent>
  );
}

function RegistryTestItemMobile({
  test,
  checked,
  onSelect,
}: {
  test: any;
  checked: boolean;
  onSelect: () => void;
}) {
  const [showQuestions, setShowQuestions] = useState(false);

  return (
    <div className="rounded-lg bg-[#F3F4F6] p-4">
      <div className="flex items-start justify-between gap-3">
        <button type="button" onClick={onSelect} className="flex min-w-0 grow items-start gap-3 text-left">
          <RadioIndicator checked={checked} />
          <div className="min-w-0 grow">
            <div className="break-words text-[16px] font-medium leading-[24px] text-[#111827]">
              {test.Title}
            </div>
            <div className="mt-2 text-[14px] leading-[20px] text-[#637381]">
              <span>{test.Collections?.Title}</span>
              <span className="mx-2">|</span>
              <span>{test.QuestionsID?.length ?? 0} вопросов</span>
            </div>
          </div>
        </button>

        <button
          type="button"
          onClick={() => setShowQuestions((prev) => !prev)}
          className="shrink-0 text-[#637381]"
        >
          {showQuestions ? (
            <ChevronUpIcon className="h-6 w-6" />
          ) : (
            <ChevronDownIcon className="h-6 w-6" />
          )}
        </button>
      </div>

      {showQuestions && (
        <div className="mt-3 space-y-2 pl-8 text-[14px] leading-[20px] text-[#637381]">
          {test.QuestionsID?.map((question: any) => (
            <div key={question.ID ?? question.Title} className="break-words">
              {question.Title}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function RadioIndicator({ checked }: { checked: boolean }) {
  return (
    <span
      className={twMerge(
        checked ? "border-darkblue" : "border-[#DFE4EA]",
        "mt-1 flex h-5 w-5 shrink-0 items-center justify-center rounded-full border bg-white",
      )}
    >
      <span
        className={twMerge(
          checked ? "bg-darkblue opacity-100" : "opacity-0",
          "h-2.5 w-2.5 rounded-full",
        )}
      />
    </span>
  );
}

export function getStoredQuestionsCount() {
  return parseStoredQuestions().length;
}
